"use client";

import { c as _c } from "react/compiler-runtime";
import LocaleLink from "@components/LocaleLink";
import { Skeleton } from "@justhome/react-ui";
import { useLinkStatus } from "next/link";
import { type ComponentProps, createContext, type MouseEvent, type PropsWithChildren, type ReactNode, useContext, useEffect, useState } from "react";

// A prefetched page swaps in faster than the eye registers, and the new cards read as the old
// ones. The skeleton stays at least this long, so the list visibly goes and comes back.
export const MIN_SKELETON_MS = 350;

// When a page link was followed. The next page mounts a fresh list, which reads it to hold the
// skeleton for the rest of the minimum.
let pagedAt: number | null = null;
const PendingContext = createContext<(pending: boolean) => void>(() => {});
const ReportPending = () => {
  const $ = _c(4);
  const {
    pending
  } = useLinkStatus();
  const report = useContext(PendingContext);
  let t0;
  let t1;
  if ($[0] !== pending || $[1] !== report) {
    t0 = () => {
      report(pending);
    };
    t1 = [pending, report];
    $[0] = pending;
    $[1] = report;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  return null;
};
const isPlainClick = (event: MouseEvent) => event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
export const PageLink = t0 => {
  const $ = _c(10);
  let children;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = props;
  } else {
    children = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== props) {
    t1 = event => {
      if (isPlainClick(event) && !props["aria-current"]) {
        pagedAt = performance.now();
      }
    };
    $[3] = props;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ReportPending />;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== children || $[7] !== props || $[8] !== t1) {
    t3 = <LocaleLink {...props} onClick={t1}>{children}{t2}</LocaleLink>;
    $[6] = children;
    $[7] = props;
    $[8] = t1;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
};
const SkeletonCard = () => {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Skeleton isLoading={true} className="aspect-[16/9] rounded-none" width="100%" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <li className="flex flex-col h-full bg-neutral-0 border border-neutral-50 rounded-m overflow-hidden">{t0}<div className="flex flex-col gap-2xs p-3s"><Skeleton isLoading={true} width="33%" height={14} /><Skeleton isLoading={true} width="100%" height={20} /><Skeleton isLoading={true} width="66%" height={20} /><Skeleton isLoading={true} className="mt-3s" width="25%" height={12} /></div></li>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const PagedList = t0 => {
  const $ = _c(16);
  const {
    count,
    pagination,
    children
  } = t0;
  const [pending, setPending] = useState(false);
  const [holdUntil] = useState(_temp);
  let t1;
  if ($[0] !== holdUntil) {
    t1 = () => holdUntil > performance.now();
    $[0] = holdUntil;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [holding, setHolding] = useState(t1);
  let t2;
  let t3;
  if ($[2] !== holdUntil || $[3] !== holding) {
    t2 = () => {
      pagedAt = null;
      if (!holding) {
        return;
      }
      const timer = window.setTimeout(() => setHolding(false), holdUntil - performance.now());
      return () => window.clearTimeout(timer);
    };
    t3 = [holding, holdUntil];
    $[2] = holdUntil;
    $[3] = holding;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  const loading = pending || holding;
  let t4;
  if ($[6] !== children || $[7] !== count || $[8] !== loading) {
    t4 = loading ? <ul aria-hidden="true" className="mt-2m grid gap-3s s:grid-cols-2 m:grid-cols-4 items-stretch list-none p-none m-none">{Array.from({
        length: count
      }, _temp2)}</ul> : children;
    $[6] = children;
    $[7] = count;
    $[8] = loading;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== loading || $[11] !== t4) {
    t5 = <div aria-busy={loading}>{t4}</div>;
    $[10] = loading;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== pagination || $[14] !== t5) {
    t6 = <PendingContext value={setPending}>{t5}{pagination}</PendingContext>;
    $[13] = pagination;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
};
export default PagedList;
function _temp() {
  return pagedAt === null ? 0 : pagedAt + MIN_SKELETON_MS;
}
function _temp2(_, index) {
  return <SkeletonCard key={index} />;
}